import { z } from 'zod'

import { Pet } from '@monorepo/functions/src/types/reservations'

export interface DateRange {
    from: Date
    to: Date
}

export interface PetFormData {
    /** Stable id for React list keys; not sent to backend */
    id?: string
    name: string
    breed: string
    weight: number
    size: 'pequeño' | 'mediano' | 'grande'
    age: string
    personality?: string
    // Campos de personalidad separados
    personalityWithOtherDogs?: string
    personalityWithStrangers?: string
    personalityFears?: string
    sex: 'M' | 'F'
    isNeutered: boolean
}

export type PetData = Omit<Pet, 'id' | 'additionalServices' | 'roomNumber'>

export const petFormSchema = z
    .object({
        id: z.string().optional(),
        name: z.string().min(1, { message: 'booking.step1.petFields.nameRequired' }),
        breed: z.string().min(1, { message: 'booking.step1.petFields.breedRequired' }),
        weight: z
            .number({
                invalid_type_error: 'booking.step1.petFields.weightRequired',
            })
            .min(1, {
                message: 'booking.step1.petFields.weightRequired',
            }),
        size: z.enum(['pequeño', 'mediano', 'grande']),
        age: z.string().min(1, { message: 'booking.step1.petFields.ageRequired' }),
        personality: z.string().optional(),
        // Campos de personalidad separados (opcionales, pero al menos uno debe estar presente)
        personalityWithOtherDogs: z.string().optional(),
        personalityWithStrangers: z.string().optional(),
        personalityFears: z.string().optional(),
        sex: z.enum(['M', 'F']),
        isNeutered: z.boolean(),
    })
    .refine(
        data => {
            // Si tiene personality con contenido, está bien (mascota antigua)
            if (data.personality && data.personality.trim().length > 0) return true
            // Si no tiene personality, debe tener todos los campos separados
            return !!(
                data.personalityWithOtherDogs &&
                data.personalityWithOtherDogs.trim().length > 0 &&
                data.personalityWithStrangers &&
                data.personalityWithStrangers.trim().length > 0 &&
                data.personalityFears &&
                data.personalityFears.trim().length > 0
            )
        },
        {
            message: 'booking.step1.petFields.personalityRequired',
            path: ['personality'],
        },
    )

export const bookingFormSchema = z
    .object({
        pets: z.array(petFormSchema),
        dates: z.any(),
        services: z.array(z.any()),
        clientName: z.string().min(1, { message: 'booking.step4.clientName.required' }),
        clientLastName: z.string().min(1, { message: 'booking.step4.clientLastName.required' }),
        clientEmail: z.string().email({ message: 'booking.step4.clientEmail.invalid' }),
        clientPhone: z.string().regex(/^(?:\+)?[0-9]{1,}$/, { message: 'booking.step4.clientPhone.invalid' }),
        clientAddress: z.string().optional(),
        // Campos de dirección separados (opcionales, solo para clientes nuevos)
        clientStreet: z.string().optional(),
        clientStreetNumber: z.string().optional(),
        clientCity: z.string().optional(),
        clientPostalCode: z.string().optional(),
        clientProvince: z.string().optional(),
        clientCountry: z.string().optional(),
    })
    .refine(
        data => {
            // Si tiene clientAddress con contenido, está bien (cliente antiguo)
            if (data.clientAddress && data.clientAddress.trim().length > 0) return true
            // Si no tiene clientAddress, debe tener todos los campos separados
            return !!(
                data.clientStreet &&
                data.clientStreet.trim().length > 0 &&
                data.clientStreetNumber &&
                data.clientStreetNumber.trim().length > 0 &&
                data.clientCity &&
                data.clientCity.trim().length > 0 &&
                data.clientPostalCode &&
                data.clientPostalCode.trim().length > 0 &&
                data.clientProvince &&
                data.clientProvince.trim().length > 0 &&
                data.clientCountry &&
                data.clientCountry.trim().length > 0
            )
        },
        {
            message: 'booking.step4.clientAddress.required',
            path: ['clientAddress'],
        },
    )

export type BookingFormData = z.infer<typeof bookingFormSchema>

export interface BookingState {
    currentStep: number
    selectedDates: DateRange | null
    totalPrice: number
    dateError: string
    formError: string
    groomingUnavailable: boolean
    checkInTime: string
    checkOutTime: string
    confirmedReservationId: string
}

export const BASE_PRICE_PER_DAY = 25
export const LARGE_DOG_SURCHARGE = 5
export const MEDIUM_DOG_SURCHARGE = 3
